import { useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { effectiveCapitalApi } from '../../helpers/effectiveCapitalApi';

function fmt(n) {
  if (n == null || n === '') return '—';
  const num = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

function fmtTime(d) {
  if (!d) return null;
  return new Date(d).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Los_Angeles',
  });
}

function toNum(v) {
  if (v == null) return 0;
  return parseFloat(String(v)) || 0;
}

function isExcluded(a) {
  return a.excludeFromTotals === true || Number(a.excludeFromTotals) === 1;
}

function isCanceledRequest(error) {
  return error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError' ||
    error?.message === 'canceled';
}

function AccountName({ a }) {
  const display = a.customName || a.name;
  return (
    <div className="flex items-baseline gap-1.5">
      <div className="text-[13px] font-medium leading-tight">{display}</div>
      {a.mask && <div className="text-[11px] text-gray-500 shrink-0">••{a.mask}</div>}
      {a._isOverride && (
        <div className="text-[9px] font-medium text-amber-600 uppercase tracking-wide shrink-0">Manual</div>
      )}
      {isExcluded(a) && (
        <div className="text-[9px] font-medium text-amber-600 uppercase tracking-wide shrink-0">Excluded</div>
      )}
    </div>
  );
}

function UtilBar({ pct }) {
  const clamped = Math.min(pct, 100);
  const color =
    clamped >= 95 ? 'bg-red-500' :
    clamped >= 85 ? 'bg-amber-500' :
    clamped >= 70 ? 'bg-yellow-400' :
    'bg-emerald-500';
  return (
    <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden mt-1">
      <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

function SectionHeader({ label, total }) {
  return (
    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-y border-gray-200">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">{label}</span>
      {total != null && (
        <span className="text-[11px] font-semibold tabular-nums">{fmt(total)}</span>
      )}
    </div>
  );
}

function ccBalance(a) {
  const limit = toNum(a.creditLimit);
  const avail = a.availableBalance != null && a.availableBalance !== 'NULL' ? toNum(a.availableBalance) : null;
  return (avail !== null && limit > 0) ? limit - avail : toNum(a.currentBalance);
}

function EntitySection({ label, accounts }) {
  const depository = accounts.filter((a) => a.type === 'depository');
  const credit = accounts.filter((a) => a.type === 'credit');
  const loan = accounts.filter((a) => a.type === 'loan' || a.subtype === 'line of credit');

  const totalCash = depository.filter((a) => !isExcluded(a))
    .reduce((s, a) => s + toNum(a.availableBalance), 0);
  const totalCC = credit.filter((a) => !isExcluded(a))
    .reduce((s, a) => s + ccBalance(a), 0);

  return (
    <div className="mb-2">
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="text-[11px] font-bold uppercase tracking-widest">{label}</div>
      </div>

      {depository.length > 0 && (
        <>
          <SectionHeader label="Cash Accounts" total={totalCash} />
          {depository.map((a) => (
            <div key={a.plaidAccountId} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors">
              <AccountName a={a} />
              <div className="text-right">
                <div className="text-[13px] font-semibold tabular-nums">{fmt(a.availableBalance)}</div>
                {a.lastSyncedAt && (
                  <div className="text-[10px] text-gray-500">{fmtTime(a.lastSyncedAt)}</div>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {credit.length > 0 && (
        <>
          <SectionHeader label="Credit Cards" total={-totalCC} />
          {credit.map((a) => {
            const limit = toNum(a.creditLimit);
            const avail = a.availableBalance != null && a.availableBalance !== 'NULL' ? toNum(a.availableBalance) : null;
            const balance = ccBalance(a);
            const pct = limit > 0 ? (balance / limit) * 100 : 0;
            return (
              <div key={a.plaidAccountId} className="px-4 py-2.5 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <AccountName a={a} />
                  <div className="text-right shrink-0">
                    <div className="text-[13px] font-semibold tabular-nums text-red-600">({fmt(balance)})</div>
                    {limit > 0 && (
                      <div className="text-[10px] text-gray-500">{fmt(avail)} avail</div>
                    )}
                  </div>
                </div>
                {limit > 0 && <UtilBar pct={pct} />}
              </div>
            );
          })}
        </>
      )}

      {loan.length > 0 && (
        <>
          <SectionHeader label="Lines of Credit" />
          {loan.map((a) => (
            <div key={a.plaidAccountId} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors">
              <AccountName a={a} />
              <div className="text-right">
                <div className="text-[13px] font-semibold tabular-nums">{fmt(a.availableBalance)}</div>
                <div className="text-[10px] text-gray-500">available</div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export const BankBalancesContent = ({ open = true, onLoadingChange, viewSwitcher }) => {
  const [accounts, setAccounts] = useState([]);
  const [balanceOverrides, setBalanceOverrides] = useState({});
  const [flexOverride, setFlexOverride] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const result = await effectiveCapitalApi.syncBalances();
      toast.success(result?.message || 'Balances synced', { position: 'bottom-right' });
      setAccounts([]);
      setIsLoading(true);
      const [accts, overrides, flex] = await Promise.all([
        effectiveCapitalApi.listAccounts(),
        effectiveCapitalApi.listBalanceOverrides().catch(() => ({})),
        effectiveCapitalApi.getFlexCreditOverride().catch(() => null),
      ]);
      setAccounts(accts || []);
      setBalanceOverrides(overrides || {});
      setFlexOverride(flex);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Balance sync failed', { position: 'bottom-right' });
    } finally {
      setIsLoading(false);
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setIsLoading(true);
    Promise.all([
      effectiveCapitalApi.listAccounts(),
      effectiveCapitalApi.listBalanceOverrides().catch(() => ({})),
      effectiveCapitalApi.getFlexCreditOverride().catch(() => null),
    ])
      .then(([accts, overrides, flex]) => {
        if (cancelled) return;
        setAccounts(accts || []);
        setBalanceOverrides(overrides || {});
        setFlexOverride(flex);
      })
      .catch((error) => {
        if (cancelled || isCanceledRequest(error)) return;
        toast.error(error?.response?.data?.error || 'Failed to load balances', { position: 'bottom-right' });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  if (!open) return null;

  const patchedAccounts = accounts
    .map((a) => {
      if (a.plaidAccountId in balanceOverrides) {
        const overrideBalance = balanceOverrides[a.plaidAccountId];
        if (a.type === 'credit') {
          const limit = toNum(a.creditLimit);
          const overrideAvail = limit > 0 ? limit - (overrideBalance ?? 0) : null;
          return { ...a, currentBalance: overrideBalance, availableBalance: overrideAvail, _isOverride: true };
        }
        return { ...a, availableBalance: overrideBalance, _isOverride: true };
      }
      return a;
    })
    .map((a) => {
      if (
        flexOverride?.amount != null &&
        a.type === 'credit' &&
        a.entity === 'kings_loot' &&
        (a.customName ?? a.name ?? '').toLowerCase().includes('flex')
      ) {
        const limit = toNum(a.creditLimit);
        const overrideBalance = toNum(flexOverride.amount);
        const overrideAvail = limit > 0 ? limit - overrideBalance : null;
        return { ...a, currentBalance: overrideBalance, availableBalance: overrideAvail, _isOverride: true };
      }
      return a;
    });

  const kl = patchedAccounts.filter((a) => a.entity === 'kings_loot');
  const kh = patchedAccounts.filter((a) => a.entity === 'kings_holding');

  const totalCash = patchedAccounts
    .filter((a) => a.type === 'depository' && !isExcluded(a))
    .reduce((s, a) => s + toNum(a.availableBalance), 0);

  const totalCC = patchedAccounts
    .filter((a) => a.type === 'credit' && !isExcluded(a))
    .reduce((s, a) => s + ccBalance(a), 0);

  const lastSync = accounts.find((a) => a.lastSyncedAt)?.lastSyncedAt;

  return (
      <div className="flex h-full min-h-[420px] flex-col overflow-hidden bg-white">
        <div className="flex shrink-0 flex-col border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            {viewSwitcher || <div className="text-[14px] font-semibold tracking-tight">Bank Account Balances</div>}
            <div className="flex items-center gap-2">
              <button type="button" onClick={handleSync} disabled={isSyncing} className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-[11px] font-medium disabled:opacity-50">
                <ArrowPathIcon className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'Syncing...' : 'Sync Balances'}
              </button>
            </div>
          </div>
          {lastSync && (
            <p className="mt-0.5 text-[11px] text-gray-500">Last sync: {fmtTime(lastSync)} PT</p>
          )}
        </div>

        <div className="grid shrink-0 grid-cols-2 divide-x divide-gray-200 border-b border-gray-200">
          <div className="px-4 py-3">
            <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-500">Total Cash</div>
            <div className="text-[15px] font-bold tabular-nums">{fmt(totalCash)}</div>
          </div>
          <div className="px-4 py-3">
            <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-500">CC Liabilities</div>
            <div className="text-[15px] font-bold tabular-nums text-red-600">({fmt(totalCC)})</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center text-[12px] text-gray-500">
              Loading balances…
            </div>
          ) : (
            <>
              {kl.length > 0 && <EntitySection label="Kings Loot" accounts={kl} />}
              {kh.length > 0 && <EntitySection label="Kings Holding" accounts={kh} />}
            </>
          )}
        </div>
      </div>
  );
};
